import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  TextField,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Chip,
  Autocomplete,
  InputAdornment,
} from '@mui/material';
import dayjs from 'dayjs';
import { DateTimePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import TuneIcon from '@mui/icons-material/Tune';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { useDispatch, useSelector } from 'react-redux';
import { SelectChangeEvent } from '@mui/material/Select';
import MainLayout from '../../components/MainLayout';
import { CustomizedSelect } from '../../styles/select';
import { ResponsiveHeading } from '../../styles/heading';
import {
  cyanBlueBackgroundButtonStyles,
  lightBackgroundButtonStyles,
} from '../../styles/button';
import { CustomizedTextField } from '../../styles/textField';

import ReadOnlyTable from '../../components/Table/readOnlyTable';
import { handleColumnwiseSearch } from '../../utils/Groups/groupUtility';
import {
  updateEmailAddresses,
  updateAppAccesses,
  updateCasbApp,
  updateIPAddresses,
  updateMacAddresses,
  updateStatusArray,
  updateSelectedDateButton,
  resetStatusArray,
  resetAppAccesses,
  resetCasbApp,
  resetEmailAddresses,
  resetIPAddresses,
  resetMacAddresses,
  updateEndTime,
  updateStartTime,
  updateFilterBox,
  resetPage,
  setFilterRows,
  resetStartTime,
  resetEndTime,
  resetSelectedDateButton,
  setApiType,
} from '../../Redux/Slices/Auditing/auditing';
import { AppDispatch, RootState } from '../../Redux/store';
import { fetchRestrictionPerDay } from '../../Redux/Slices/Auditing/auditingHandler';
import { statusMapping } from '../ApplicationInsights/appInsights';

const menuButtonStyles = {
  fontSize: '2rem',
  color: 'gray',
  cursor: 'pointer',
  transition: 'color 0.3s ease, transform 0.2s ease',
};
type FieldOption = {
  value: string;
  key: string;
};
export const getKeysFromValues = (values: string[]): string[] => {
  const keys = Object.keys(statusMapping);
  const result: string[] = [];

  values.forEach((value) => {
    const matchingKey = keys.find(
      (key) => statusMapping[key].trim().toLowerCase() === value.trim().toLowerCase(),
    );
    if (matchingKey) {
      result.push(matchingKey);
    }
  });

  return result;
};

export function resetState(dispatch: AppDispatch) {
  dispatch(resetIPAddresses());
  dispatch(resetPage());
  dispatch(resetEmailAddresses());
  dispatch(resetCasbApp());
  dispatch(resetAppAccesses());
  dispatch(resetMacAddresses());
  dispatch(resetStatusArray());
  dispatch(resetStartTime());
  dispatch(resetEndTime());
  dispatch(resetSelectedDateButton());
  dispatch(setApiType('fields'));
  dispatch(setFilterRows([{ id: Date.now() }]));
}

export default function UserInsights() {
  const dispatch = useDispatch<AppDispatch>();
  const {
    totalUsers,
    selectedDateButton,
    startTime,
    endTime,
    showFilterBox,
    filterRows,
  } = useSelector((state: RootState) => state.auditingDashboard);
  const updatedUsers = totalUsers.map((user) => ({
    ...user,
    status: statusMapping[user.status] || user.status,
  }));

  const { currentActiveTenantName } = useSelector(
    (state: RootState) => state.customer,
  );

  const [filteredUsers, setFilteredUsers] = useState(updatedUsers);
  const [searchValue, setSearchValue] = useState('');
  const [selectedColumn, setSelectedColumn] = useState('');
  const [fromDate] = useState<string>('');
  const [toDate] = useState<string>('');

  useEffect(() => {
    if (currentActiveTenantName !== '') {
      dispatch(setApiType('fields'));
      dispatch(fetchRestrictionPerDay());
    }
  }, [currentActiveTenantName]);

  let filteredUsersData;
  useEffect(() => {
    filteredUsersData = totalUsers.map((user) => ({
      ...user,
      status: statusMapping[user.status] || user.status,
    }));
    setFilteredUsers(filteredUsersData);
  }, [totalUsers]);

  const dateOptions = [
    { label: 'Today', value: 0 },
    { label: 'Yesterday', value: 1 },
    { label: 'Last Week', value: 7 },
    { label: 'Last Month', value: 30 },
    { label: 'Last Year', value: 365 },
  ];

  const fieldOptions: FieldOption[] = [
    { key: 'User', value: 'user' },
    { key: 'IP Address', value: 'ip' },
    { key: 'Accessed Apps', value: 'appAccessed' },
    { key: 'CASB App Service', value: 'casbApp' },
    { key: 'Restriction Type', value: 'status' },
  ];

  const handleFilterIconClick = () => {
    dispatch(updateFilterBox(!showFilterBox));
  };
  const handleClearAll = () => {
    resetState(dispatch);
  };
  const handleAddNewFilterRow = () => {
    // setFilterRows([...filterRows, { id: Date.now() }]);
    dispatch(setFilterRows([...filterRows, { id: Date.now() }]));
  };

  const handleSingleSelectChange = (
    event: SelectChangeEvent<string>,
    index: number,
  ) => {
    const selectedValue = event.target.value;
    const updatedRows = [...filterRows];
    const previousField = updatedRows[index]?.selectedField;
    if (previousField) {
      switch (previousField) {
        case 'user':
          dispatch(resetEmailAddresses());
          break;
        case 'ip':
          dispatch(resetIPAddresses());
          break;
        case 'appAccessed':
          dispatch(resetAppAccesses());
          break;
        case 'casbApp':
          dispatch(resetCasbApp());
          break;
        case 'macAddresses':
          dispatch(resetMacAddresses());
          break;
        case 'status':
          dispatch(resetStatusArray());
          break;
        default:
          console.warn('Unhandled field for reset:', previousField);
      }
    }

    updatedRows[index] = {
      ...updatedRows[index],
      selectedField: selectedValue,
      chipList: [],
    };

    dispatch(setFilterRows(updatedRows));
  };
  const handleDateSelection = (days: number) => {
    const now = dayjs();
    let selectedFromDate = dayjs();
    switch (days) {
      case 0:
        selectedFromDate = now.startOf('day');
        break;
      case 1:
        selectedFromDate = now.subtract(1, 'day');
        break;
      case 7:
        selectedFromDate = now.subtract(7, 'days');
        break;
      case 30:
        selectedFromDate = now.subtract(1, 'month');
        break;
      case 365:
        selectedFromDate = now.subtract(1, 'year');
        break;
      default:
        break;
    }
    const formattedStartTime = selectedFromDate.format('YYYY-MM-DD HH:mm:ss');
    const formattedEndDate = now.format('YYYY-MM-DD HH:mm:ss');
    dispatch(updateSelectedDateButton(days));
    dispatch(updateStartTime(formattedStartTime.toString()));
    dispatch(updateEndTime(formattedEndDate.toString()));
  };

  const handleDeleteFilterRow = (rowId: number) => {
    const rowToDelete = filterRows.find((row) => row.id === rowId);

    if (rowToDelete) {
      switch (rowToDelete.selectedField) {
        case 'user':
          dispatch(resetEmailAddresses());
          break;
        case 'ip':
          dispatch(resetIPAddresses());
          break;
        case 'appAccessed':
          dispatch(resetAppAccesses());
          break;
        case 'casbApp':
          dispatch(resetCasbApp());
          break;
        case 'status':
          dispatch(resetStatusArray());
          break;
        case 'macAddress':
          dispatch(resetMacAddresses());
          break;
        default:
          break;
      }
    }
    dispatch(setFilterRows(filterRows.filter((row) => row.id !== rowId)));
  };

  useEffect(() => {
    const updateRowsWithDate = (
      prevRowsData: any[],
      fromDateData: any,
      toDateData: any,
    ): any[] => prevRowsData.map((row) => ({
      ...row,
      fromDateData,
      toDateData,
    }));
    const updatedRows = updateRowsWithDate(filterRows, fromDate, toDate);
    dispatch(setFilterRows(updatedRows));
  }, [fromDate, toDate, dispatch]);

  const handleChipChange = (
    event: React.ChangeEvent<{}>,
    newValue: string[],
    index: number,
  ) => {
    const { selectedField } = filterRows[index] as {
      selectedField?: string;
      chipList: any[];
    };
    switch (selectedField) {
      case 'user':
        dispatch(updateEmailAddresses(newValue));
        break;
      case 'ip':
        dispatch(updateIPAddresses(newValue));
        break;
      case 'appAccessed':
        dispatch(updateAppAccesses(newValue));
        break;
      case 'casbApp':
        dispatch(updateCasbApp(newValue));
        break;
      case 'macAddresses':
        dispatch(updateMacAddresses(newValue));
        break;
      case 'status': {
        const formattedNewValue = getKeysFromValues(newValue);
        dispatch(updateStatusArray(formattedNewValue));
        break;
      }
      default:
        console.warn('Unhandled field:', selectedField);
    }

    const updateRowChipList = (
      prevRowsData: any[],
      indexData: number,
      newValueData: any,
    ): any[] => {
      const updatedRows = [...prevRowsData];
      updatedRows[indexData] = {
        ...updatedRows[indexData],
        chipList: newValueData,
      };
      return updatedRows;
    };
    const updatedRows1 = updateRowChipList(filterRows, index, newValue);
    dispatch(setFilterRows(updatedRows1));

    const updatedRows = [...filterRows];
    updatedRows[index] = { ...updatedRows[index], chipList: newValue };
  };

  const handleAdvancedSearch = () => {
    dispatch(resetPage());
    dispatch(fetchRestrictionPerDay());
  };

  return (
    <MainLayout>
      <Box
        sx={{
          height: { md: '90vh', xxl: '90vh' },
          overflowY: 'auto',
          padding: 2,
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Box p={1} width="100%">
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              // mb={1}
              marginLeft={5}
            >
              <ResponsiveHeading>User Insights</ResponsiveHeading>
            </Box>
          </Box>
        </Box>
        <Box marginLeft={5} marginRight={2}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            gap="5rem"
          >
            <Box display="flex" alignItems="center" marginBottom={2}>
              <Box>
                <CustomizedTextField
                  id="input-with-icon-textfield"
                  size="small"
                  variant="outlined"
                  placeholder="Search for user details"
                  onChange={(event: any) => {
                    const searchTerm = event.target.value;
                    setSearchValue(searchTerm);
                    handleColumnwiseSearch(
                      searchTerm,
                      updatedUsers,
                      filteredUsers,
                      setFilteredUsers,
                      selectedColumn,
                    );
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon
                          sx={{
                            fontSize: { xxl: '2rem', xl: '1.5rem' },
                            padding: '0rem !important',
                          }}
                        />
                      </InputAdornment>
                    ),
                    sx: {
                      height: {
                        xxl: '2.813rem !important',
                        xl: '2.625rem !important',
                        lg: '3.75rem !important',
                      },
                      width: '20rem',
                      color: '#474747 !important',
                      borderRadius: '0.313rem',
                    },
                  }}
                />
              </Box>
              <Box
                sx={{
                  marginTop: 1,
                  marginBottom: {
                    xl: 0,
                    xxl: 1,
                    lg: 1,
                  },
                }}
              >
                <CustomizedSelect
                  value={selectedColumn || 'All'}
                  onChange={(event: React.ChangeEvent<{ value: unknown }>) => {
                    const selectedValue = event.target.value as string;
                    setSelectedColumn(selectedValue);
                    handleColumnwiseSearch(
                      searchValue,
                      updatedUsers,
                      filteredUsers,
                      setFilteredUsers,
                      selectedValue,
                    );
                  }}
                  sx={{
                    ml: 2,
                    minHeight: { xxl: '0.938rem !important' },
                    color: '#474747 !important',
                  }}
                >
                  <MenuItem value="All">All</MenuItem>
                  {fieldOptions.map((field) => (
                    <MenuItem key={field.value} value={field.value}>
                      {field.key}
                    </MenuItem>
                  ))}
                </CustomizedSelect>
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'end',
                alignItems: 'center',
                gap: '2rem',
                marginRight: '2rem',
              }}
            >
              <TuneIcon
                onClick={handleFilterIconClick}
                sx={{
                  ...menuButtonStyles,
                  color: '#517bf9',
                }}
              />
            </Box>
          </Box>
          {showFilterBox && (
            <Box
              sx={{
                border: '0.063rem solid #e5e5e5',
                backgroundColor: '#fcfcfc',
                borderRadius: 1,
                padding: 2,
                marginBlock: 2,
                marginRight: 3,
              }}
            >
              <Box gap={2}>
                {/* <ResponsiveSubHeading>Advanced Filter</ResponsiveSubHeading> */}
              </Box>
              <Box display="flex" alignItems="center" gap={5} marginBlock={3}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Box
                    sx={{
                      width: {
                        xxl: 'auto',
                        md: 'auto',
                        lg: '11.25rem',
                        xl: '15rem',
                      },
                    }}
                  >
                    <DateTimePicker
                      label="From Date and Time"
                      value={startTime ? dayjs(startTime) : null}
                      onChange={(newValue) => {
                        if (newValue && newValue.isValid()) {
                          const formattedStartTime = newValue.format(
                            'YYYY-MM-DD HH:mm:ss',
                          );
                          dispatch(updateStartTime(formattedStartTime));
                        }
                      }}
                      {...(window.innerWidth >= 1200
                        && window.innerWidth <= 1400 && { label: 'From Date' })}
                      slotProps={{ textField: { size: 'small' } }}
                    />
                  </Box>
                  <Box
                    sx={{
                      width: {
                        xxl: 'auto',
                        md: 'auto',
                        lg: '11.25rem',
                        xl: '15rem',
                      },
                    }}
                  >
                    <DateTimePicker
                      label="To Date and Time"
                      value={endTime ? dayjs(endTime) : null}
                      onChange={(newValue) => {
                        if (newValue && newValue.isValid()) {
                          const formattedEndTime = newValue.format(
                            'YYYY-MM-DD HH:mm:ss',
                          );
                          dispatch(updateEndTime(formattedEndTime));
                        }
                      }}
                      {...(window.innerWidth >= 1200
                        && window.innerWidth <= 1400 && { label: 'To Date' })}
                      slotProps={{ textField: { size: 'small' } }}
                    />
                  </Box>
                </LocalizationProvider>
                <Box>
                  <ButtonGroup
                    variant="outlined"
                    aria-label="date selection group"
                  >
                    {dateOptions.map((option) => (
                      <Button
                        key={option.value}
                        onClick={() => handleDateSelection(option.value)}
                        sx={{
                          backgroundColor:
                            selectedDateButton === option.value
                              ? '#0052CC'
                              : 'inherit',
                          color:
                            selectedDateButton === option.value
                              ? '#FFFFFF'
                              : '#0052CC',
                          border: '0.063rem solid #0052CC',
                          // fontWeight : 'bold',
                          borderRadius: 'none !important',
                          '&:hover': {
                            backgroundColor:
                              selectedDateButton === option.value
                                ? '#0052CC'
                                : 'inherit',
                            color:
                              selectedDateButton === option.value
                                ? '#FFFFFF'
                                : '#0052CC',
                          },
                        }}
                      >
                        {option.label}
                      </Button>
                    ))}
                  </ButtonGroup>
                </Box>
              </Box>
              {filterRows.map((row, index) => (
                <Box
                  display="flex"
                  alignItems="center"
                  gap={2}
                  sx={{ marginBlock: 2 }}
                  key={row.id}
                >
                  <FormControl
                    sx={{
                      width: '30%',
                      '& .MuiInputBase-root': {
                        height: '2.813rem',
                        backgroundColor: '#ffffff',
                        color: '#474747',
                      },
                    }}
                  >
                    <InputLabel id={`filter-select-label-${row.id}`} />
                    <Select
                      key={row.id}
                      labelId={`filter-select-label-${row.id}`}
                      value={row.selectedField || ''}
                      onChange={(e) => handleSingleSelectChange(e, index)}
                      size="small"
                      displayEmpty
                      renderValue={(selected) => {
                        if (!selected) {
                          return 'Select field';
                        }
                        return (
                          fieldOptions.find((field) => field.value === selected)
                            ?.key || selected
                        );
                      }}
                    >
                      {fieldOptions
                        .filter(
                          (field) => !filterRows.some(
                            (r) => r.selectedField === field.value && r !== row,
                          ),
                        )
                        .map((field) => (
                          <MenuItem key={field.value} value={field.value}>
                            {field.key}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>

                  <Autocomplete
                    sx={{
                      width: '50%',
                      '& .MuiInputBase-root': {
                        height: '2.813rem',
                        backgroundColor: '#ffffff',
                        alignItems: 'center',
                      },
                      '& .MuiAutocomplete-inputRoot': {
                        minHeight: '2.813rem',
                        flexWrap: 'wrap',
                      },
                      '& .MuiAutocomplete-endAdornment': {
                        alignSelf: 'center',
                      },
                    }}
                    multiple
                    freeSolo
                    size="small"
                    fullWidth
                    id="chips-autocomplete"
                    value={row.chipList || []}
                    onChange={(event, newValue) => handleChipChange(event, newValue, index)}
                    options={
                      row.selectedField !== 'status'
                        ? []
                        : Object.values(statusMapping)
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        placeholder="Type and press Enter to add"
                        InputProps={{
                          ...params.InputProps,
                          style: { height: '100%' },
                        }}
                      />
                    )}
                    renderTags={(value, getTagProps) => value.map((option, tagIndex) => {
                      const { key, ...tagProps } = getTagProps({
                        index: tagIndex,
                      });
                      return (
                        <Chip
                          key={option.id || option}
                          size="medium"
                          label={option}
                          {...tagProps}
                        />
                      );
                    })}
                  />

                  {filterRows.length > 1 && (
                    <IconButton onClick={() => handleDeleteFilterRow(row.id)}>
                      <DeleteOutlineIcon />
                    </IconButton>
                  )}
                </Box>
              ))}
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                sx={{ marginTop: 3 }}
              >
                <Box display="flex" gap={2}>
                  <Button
                    variant="text"
                    sx={{
                      ...lightBackgroundButtonStyles,
                      border: '0.094rem solid #0052CC !important',
                      fontSize: { lg: '0.875rem !important' },
                    }}
                    onClick={() => handleClearAll()}
                  >
                    Clear All
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleAddNewFilterRow}
                    disabled={filterRows.length >= fieldOptions.length}
                    sx={{
                      ...cyanBlueBackgroundButtonStyles,
                      fontSize: { lg: '0.875rem !important' },
                    }}
                  >
                    <AddIcon />
                    Add New Filter Row
                  </Button>
                </Box>
                <Box display="flex" gap={2}>
                  {/* <Button variant="text" sx={{ color: 'black' }}>
                    Export CSV
                  </Button>
                  <Button variant="text" sx={{ color: 'black' }}>
                    Export Report
                  </Button> */}
                  <Button
                    variant="outlined"
                    sx={{
                      ...cyanBlueBackgroundButtonStyles,
                      width: '5rem !important',
                      fontSize: { lg: '0.875rem !important' },
                    }}
                    onClick={() => {
                      handleAdvancedSearch();
                    }}
                  >
                    Search
                  </Button>
                </Box>
              </Box>
            </Box>
          )}
          <Box
            sx={{
              borderRadius: '1rem',
              marginRight: 3,
              marginTop: showFilterBox ? 4 : 2,
              flexGrow: 1,
            }}
          >
            <ReadOnlyTable
              data={filteredUsers}
              columns={[
                { field: 'user', headerName: 'User' },
                { field: 'ip', headerName: 'IP Addresses' },
                { field: 'casbApp', headerName: 'CASB App' },
                { field: 'appAccessed', headerName: 'Accessed Applications' },
                { field: 'status', headerName: 'Status' },
                { field: 'timestamp', headerName: 'Timestamp(In UTC)' },
              ]}
              pages="insights"
            />
          </Box>
        </Box>
      </Box>
    </MainLayout>
  );
}
